// Tier 2 Progress dashboard math helpers (#5–#8). All pure functions
// over already-fetched data. See docs/progress-redesign.md.
import { DAY_MS } from "./freshness.js";
import type { LogSet } from "./schemas/logSet.js";
import type { TimedSessionResult } from "./progressTypes.js";

// =========================================================================
// Tier 2 #5 — Reps × intensity heatmap.
// =========================================================================
// 4×4 matrix counting weighted sets by (rep range, %1RM intensity).
// Rep buckets: 1-3 / 4-6 / 7-10 / 11+
// Intensity buckets: <50% / 50-70% / 70-85% / 85%+
// Non-weighted kinds are skipped. Fail sets are NOT: they count their
// reps like any other set (AP-0p model — `reps` is already the clean
// count and `fail` is a pure label), and this function has no branch on
// the field at all.
//
// An explicit userMaxKg is required to anchor the intensity scale: with
// none (userMaxKg <= 0) the matrix comes back empty rather than falling
// back to each set's own e1rmKg. That fallback would be circular — a
// set's e1rmKg IS its 1RM estimate for that rep count, so weight/e1rmKg
// reduces to 30/(30+reps) and says nothing about how hard the set was.
// Since e1rmKg is only populated for 1..E1RM_REP_CAP (12) reps, that
// ratio can only ever land in ~71-97%: every set would read "heavy".

export type RepsIntensityMatrix = number[][]; // [repsBucket][intensityBucket]

const repsBucketIdx = (reps: number): number => {
  if (reps <= 3) return 0;
  if (reps <= 6) return 1;
  if (reps <= 10) return 2;
  return 3;
};

const intensityBucketIdx = (pct: number): number => {
  if (pct < 0.5) return 0;
  if (pct < 0.7) return 1;
  if (pct < 0.85) return 2;
  return 3;
};

export const repsIntensityBuckets = (
  sets: ReadonlyArray<LogSet>,
  userMaxKg: number,
): RepsIntensityMatrix => {
  const matrix: RepsIntensityMatrix = [
    [0, 0, 0, 0],
    [0, 0, 0, 0],
    [0, 0, 0, 0],
    [0, 0, 0, 0],
  ];
  if (userMaxKg <= 0) return matrix;
  for (const s of sets) {
    if (s.kind !== "weighted") continue;
    // A fail counts its reps (AP-0p model: reps = clean, fail = pure label).
    if (!s.reps || s.reps < 1) continue;
    if (!s.weight || s.weight <= 0) continue;
    const pct = s.weight / userMaxKg;
    if (!Number.isFinite(pct) || pct <= 0) continue;
    const row = matrix[repsBucketIdx(s.reps)];
    if (!row) continue;
    const col = intensityBucketIdx(pct);
    row[col] = (row[col] ?? 0) + 1;
  }
  return matrix;
};

// =========================================================================
// Tier 2 #6 — Density trend.
// =========================================================================
// Per-session density (kg/min) ordered chronologically. Sessions
// without a densityKgPerMin (no endedAt) are skipped — they'd plot as
// gaps and the UI's rolling-avg line handles missing data gracefully.

export type DensityPoint = { atMs: number; kgPerMin: number };

export const densityTrend = (
  sessions: TimedSessionResult[],
  nowMs: number,
  windowDays: number,
): ReadonlyArray<DensityPoint> => {
  const cutoff = nowMs - windowDays * DAY_MS;
  return sessions
    .filter(
      (s) =>
        s.startedAtMs >= cutoff &&
        s.startedAtMs <= nowMs &&
        s.result.densityKgPerMin !== null &&
        s.result.densityKgPerMin > 0,
    )
    .sort((a, b) => a.startedAtMs - b.startedAtMs)
    .map((s) => ({
      atMs: s.startedAtMs,
      kgPerMin: s.result.densityKgPerMin!,
    }));
};
